import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PanelLoading } from "@/components/layout/AppLoading";
import {
  InvoicePdfPanel,
  InvoicePdfStage,
} from "@/components/invoices/detail/InvoiceDetailChrome";
import { InvoiceDetailRightPanel } from "@/components/invoices/detail/InvoiceDetailRightPanel";
import { InvoiceRejectModal } from "@/components/invoices/detail/InvoiceRejectModal";
import { InvoiceCategoryModal } from "@/components/invoices/InvoiceCategoryModal";
import { InvoiceStatusModal } from "@/components/invoices/InvoiceStatusModal";
import { selectCurrentUser } from "@/features/auth/authSlice";
import { PERMISSIONS } from "@/features/auth/permissions";
import { useHasPermission } from "@/features/auth/useAuth";
import { useAssignees } from "@/features/invoices/useAssignees";
import { useErrorToast } from "@/hooks/use-error-toast";
import { useInvoiceFile } from "@/hooks/use-invoice-file";
import { useContainerWidth } from "@/hooks/use-container-width";
import { DETAIL_SPLIT_WIDTH } from "@/lib/responsive";
import {
  canApprove,
  canCompleteReview,
  canReject,
  getAmountErrors,
  getDetailLockReason,
  getInvoiceFormDefaults,
  getQuickChangeAccess,
  getApprovalError,
  isValidClientName,
  toUpdateInvoicePayload,
} from "@/lib/invoiceDetail";
import {
  useAddCommentMutation,
  useDeleteCommentMutation,
  useGetCommentsQuery,
  useUpdateCommentMutation,
} from "@/services/comments/commentsApi";
import {
  useApproveInvoiceMutation,
  useAssignInvoiceMutation,
  useCompleteManualReviewMutation,
  useExtractInvoiceDataMutation,
  useGetAuditsQuery,
  useGetInvoiceByGuidQuery,
  useRejectInvoiceMutation,
  useUpdateInvoiceClientMutation,
  useUpdateInvoiceMutation,
} from "@/services/invoices/invoicesApi";
import { useStarInvoiceMutation, useUnstarInvoiceMutation } from "@/services/starred/starredApi";

export function InvoiceDetailPage({ viewOnly = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const can = useHasPermission();
  const currentUser = useSelector(selectCurrentUser);
  const showErrorToast = useErrorToast();

  // Approving from the Approval Requests queue should return there, not to
  // whatever happened to be in history.
  const returnPath =
    location.state?.from === "approvalRequests"
      ? (location.state?.returnPath ?? "/approval-requests")
      : null;

  const goBack = useCallback(() => {
    if (returnPath) navigate(returnPath);
    else if (location.key === "default") navigate("/my-invoices");
    else navigate(-1);
  }, [location.key, navigate, returnPath]);

  /* --------------------------------------------------------------- queries */

  const {
    data: detail,
    isLoading,
    error: detailError,
  } = useGetInvoiceByGuidQuery(
    { guid: id, currentUserGuid: currentUser?.guid },
    { skip: !id },
  );

  const { data: comments = [], isLoading: isLoadingComments } = useGetCommentsQuery(id, {
    skip: !id,
  });

  // Skipped rather than merely hidden: without the permission the request is a
  // guaranteed 403, and firing it on every invoice open is noise in the logs.
  const canReadAudits = can(PERMISSIONS.GET_AUDITS);
  const canDownload = can(PERMISSIONS.DOWNLOAD_ATTACHMENT);
  const { data: audits, isFetching: isLoadingActivity } = useGetAuditsQuery(
    { invoiceGuid: id, page: 1, pageSize: 50 },
    { skip: !id || !canReadAudits },
  );

  // Anyone who can open the invoice for editing may hand it on, so this is not
  // gated on the update permission; the list loads when the picker is used.
  const { users, request: requestAssignees } = useAssignees();

  const file = useInvoiceFile(id);

  // Whether the document can sit beside the form is a question about this page's
  // own width, not the window's: the same 1180px viewport splits comfortably
  // with the rail collapsed and not at all with it open. Measuring the container
  // also means an iPad Pro 11" in landscape finally splits — the old
  // `min-width: 1200px` viewport rule missed it by six pixels.
  //
  // Held rather than guessed while unmeasured. The two layouts mount *different*
  // PDF components — a left panel when split, a stage inside the right panel
  // when not — so guessing either way mounts a Syncfusion viewer and tears it
  // down again on the next frame, on the layout that can least afford it.
  const [pageRef, pageWidth] = useContainerWidth();
  const isMeasured = pageWidth != null;
  const isSplit = isMeasured && pageWidth >= DETAIL_SPLIT_WIDTH;

  /* ------------------------------------------------------------- mutations */

  const [updateInvoice, { isLoading: isSavingForm }] = useUpdateInvoiceMutation();
  const [updateInvoiceClient, { isLoading: isSavingClientName }] =
    useUpdateInvoiceClientMutation();
  const [completeManualReview, { isLoading: isCompleting }] = useCompleteManualReviewMutation();
  const [approveInvoice, { isLoading: isApproving }] = useApproveInvoiceMutation();
  const [rejectInvoice, { isLoading: isRejecting }] = useRejectInvoiceMutation();
  const [extractInvoiceData, { isLoading: isExtracting }] = useExtractInvoiceDataMutation();
  const [assignInvoice] = useAssignInvoiceMutation();
  const [starInvoice, { isLoading: isStarring }] = useStarInvoiceMutation();
  const [unstarInvoice, { isLoading: isUnstarring }] = useUnstarInvoiceMutation();
  const [addComment, { isLoading: isAddingComment }] = useAddCommentMutation();
  const [updateComment, { isLoading: isUpdatingComment }] = useUpdateCommentMutation();
  const [deleteComment] = useDeleteCommentMutation();

  /* ----------------------------------------------------------- local state */

  const [form, setForm] = useState({});
  const [clientName, setClientName] = useState("");
  const [clientNameError, setClientNameError] = useState(null);
  const [approvalError, setApprovalError] = useState(null);
  // Bumped on every blocked save, so the panel scrolls to the control each time
  // even when the message itself has not changed.
  const [approvalAttempt, setApprovalAttempt] = useState(0);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const { canChangeStatus, canRecategorise } = getQuickChangeAccess({
    viewOnly,
    canUpdate: can(PERMISSIONS.UPDATE_INVOICE),
    canReadCategories: can(PERMISSIONS.INVOICE_CATEGORIES),
  });
  const [confirmAction, setConfirmAction] = useState(null);

  // The form is seeded from the server record when the record changes identity —
  // not on every refetch, which would discard whatever the user was typing.
  // Done during render rather than in an effect so the fields never paint once
  // with the previous invoice's values.
  const [seededGuid, setSeededGuid] = useState(null);
  // What the form was seeded with, or last saved. "Edited" is measured against
  // this snapshot rather than the live record: a background refetch or a value
  // the server stored in another shape (a reformatted date or amount) does not
  // change what is on screen, so it must not read as an unsaved edit.
  const [originalForm, setOriginalForm] = useState({});
  if (detail && detail.guid !== seededGuid) {
    setSeededGuid(detail.guid);
    setForm(getInvoiceFormDefaults(detail));
    setOriginalForm(getInvoiceFormDefaults(detail));
    setClientName(detail.clientName ?? "");
    setClientNameError(null);
    setApprovalError(null);
  }

  useEffect(() => {
    if (detailError) showErrorToast(detailError);
  }, [detailError, showErrorToast]);

  const activity = useMemo(() => audits?.items ?? [], [audits]);
  const isSaving = isSavingForm || isCompleting || isApproving || isRejecting;

  /* ---------------------------------------------------------------- guards */

  if (isLoading) {
    return <PanelLoading label="Loading invoice" />;
  }

  if (!detail) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="text-sm font-semibold text-foreground">Invoice not found</p>
        <Button type="button" variant="outline" onClick={goBack}>
          Go back
        </Button>
      </div>
    );
  }

  /* --------------------------------------------------------------- actions */

  // One value drives both: what the form does, and what the panel tells the
  // user when it cannot be edited. Previously the commit bar simply vanished on
  // a locked invoice, leaving a disabled form with no actions and no reason.
  const lockReason = getDetailLockReason(detail, {
    canUpdate: can(PERMISSIONS.UPDATE_INVOICE),
    viewOnly,
  });
  const readOnly = viewOnly || Boolean(lockReason);

  // Shown inline on the amount fields; they win over a stale server message.
  const amountErrors = getAmountErrors(form, originalForm);
  const fieldErrors = { ...detail?.fieldErrors, ...amountErrors };

  /** False (with a toast) when an amount is not a number, so nothing is saved. */
  const checkAmounts = () => {
    if (Object.keys(amountErrors).length === 0) return true;
    toast.error("Fix the highlighted amounts before saving.");
    return false;
  };

  /** False (inline error) when a sample invoice that is Ready For Processing has no approved / not approved choice. */
  const checkApproval = () => {
    const message = getApprovalError(detail, form);
    setApprovalError(message);
    if (message) setApprovalAttempt((count) => count + 1);
    return message === null;
  };

  /** Runs both checks so every problem is shown at once, not one per attempt. */
  const checkValid = () => {
    const amountsOk = checkAmounts();
    const approvalOk = checkApproval();
    return amountsOk && approvalOk;
  };

  const saveForm = async () => {
    await updateInvoice({
      guid: detail.guid,
      data: toUpdateInvoicePayload(form, { includeApproval: detail.isSampleInvoice }),
    }).unwrap();
    setOriginalForm(form);
  };

  const handleSave = async () => {
    if (!checkValid()) return;
    try {
      await saveForm();
      toast.success("Invoice updated");
    } catch (error) {
      showErrorToast(error);
    }
  };

  const handleComplete = async () => {
    setConfirmAction(null);
    if (!checkValid()) return;
    try {
      await saveForm();
      await completeManualReview(detail.guid).unwrap();
      toast.success("Invoice saved and review completed");
      goBack();
    } catch (error) {
      showErrorToast(error);
    }
  };

  const handleApprove = async () => {
    setConfirmAction(null);
    if (!checkValid()) return;
    try {
      await saveForm();
      await approveInvoice({ guid: detail.guid, comment: "" }).unwrap();
      toast.success("Invoice saved and approved");
      goBack();
    } catch (error) {
      showErrorToast(error);
    }
  };

  const handleReject = async ({ reason, comment }) => {
    try {
      await rejectInvoice({ guid: detail.guid, reason, comment }).unwrap();
      toast.success("Invoice rejected");
      setRejectOpen(false);
      goBack();
    } catch (error) {
      showErrorToast(error);
    }
  };

  const handleClientNameChange = (value) => {
    setClientName(value);
    setClientNameError(
      isValidClientName(value) ? null : "Use letters and spaces only.",
    );
  };

  const handleClientNameSave = async () => {
    if (!clientName.trim()) {
      setClientNameError("Client name is required.");
      return;
    }
    try {
      await updateInvoiceClient({ guid: detail.guid, clientName }).unwrap();
      toast.success("Client name updated");
    } catch (error) {
      showErrorToast(error);
    }
  };

  const handleAssigneeChange = async (invoice, userGuid) => {
    try {
      await assignInvoice({ guid: detail.guid, userGuid }).unwrap();
      const assigneeName = users.find((user) => user.guid === userGuid)?.name;
      toast.success(
        userGuid
          ? `Invoice assigned${assigneeName ? ` to ${assigneeName}` : ""}`
          : "Invoice unassigned",
      );
    } catch (error) {
      showErrorToast(error);
    }
  };

  const handleToggleStar = async () => {
    try {
      if (detail.starred) {
        await unstarInvoice(detail.guid).unwrap();
        toast.success("Removed from starred");
      } else {
        await starInvoice(detail.guid).unwrap();
        toast.success("Added to starred");
      }
    } catch (error) {
      showErrorToast(error);
    }
  };

  const handleExtract = async () => {
    try {
      await extractInvoiceData(detail.guid).unwrap();
      toast.success("Data extraction started");
    } catch (error) {
      showErrorToast(error);
    }
  };

  const handleAddComment = async (body) => {
    try {
      await addComment({ invoiceGuid: detail.guid, comment: body }).unwrap();
      toast.success("Comment posted");
    } catch (error) {
      showErrorToast(error);
    }
  };

  const handleUpdateComment = async (guid, body) => {
    try {
      await updateComment({ guid, invoiceGuid: detail.guid, comment: body }).unwrap();
      toast.success("Comment updated");
    } catch (error) {
      showErrorToast(error);
    }
  };

  const handleDeleteComment = async (guid) => {
    try {
      await deleteComment({ guid, invoiceGuid: detail.guid }).unwrap();
      toast.success("Comment deleted");
    } catch (error) {
      showErrorToast(error);
    }
  };

  // Two independent questions, and the status one was answering both: whether
  // this invoice is *in a state* to be approved, and whether *this operator* may
  // approve it. `canApprove`/`canReject`/`canCompleteReview` only ever answered
  // the first, so anyone who could open an invoice for editing could finalise it.
  const showApprove =
    !readOnly && canApprove(detail.reviewStatusId) && can(PERMISSIONS.APPROVE_INVOICE);
  const showComplete =
    !readOnly && canCompleteReview(detail.reviewStatusId) && can(PERMISSIONS.COMPLETE_MANUAL_REVIEW);
  const showReject =
    !readOnly && canReject(detail.reviewStatusId) && can(PERMISSIONS.REJECT_INVOICE);

  const documentStage = (
    <InvoicePdfStage
      fileName={detail.fileName}
      file={file}
      onDownload={canDownload ? () => file.download(detail.fileName) : undefined}
    />
  );

  return (
    <div ref={pageRef} className="invoice-detail-page" data-split={isSplit ? "true" : "false"}>
      <div className="invoice-detail-grid">
        {isSplit && (
          <InvoicePdfPanel
            fileName={detail.fileName}
            file={file}
            onDownload={canDownload ? () => file.download(detail.fileName) : undefined}
          />
        )}

        <InvoiceDetailRightPanel
          documentSlot={isMeasured && !isSplit ? documentStage : null}
          detail={detail}
          form={form}
          originalForm={originalForm}
          fieldErrors={fieldErrors}
          viewOnly={readOnly}
          isSaving={isSaving}
          showComplete={showComplete}
          showApprove={showApprove}
          showReject={showReject}
          lockReason={lockReason}
          onFormChange={(key, value) => {
            if (key === "isApproved") setApprovalError(null);
            setForm((current) => ({ ...current, [key]: value }));
          }}
          approvalError={approvalError}
          approvalAttempt={approvalAttempt}
          onBack={goBack}
          onToggleStar={handleToggleStar}
          isTogglingStar={isStarring || isUnstarring}
          onExtract={handleExtract}
          isExtracting={isExtracting}
          canExtract={can(PERMISSIONS.EXTRACT_INVOICE_DATA)}
          canEdit={can(PERMISSIONS.UPDATE_INVOICE)}
          isViewRoute={viewOnly}
          onChangeCategory={canRecategorise ? () => setCategoryOpen(true) : undefined}
          onChangeStatus={canChangeStatus ? () => setStatusOpen(true) : undefined}
          assignees={users}
          onRequestAssignees={requestAssignees}
          onAssigneeChange={handleAssigneeChange}
          clientName={clientName}
          clientNameError={clientNameError}
          onClientNameChange={handleClientNameChange}
          onClientNameSave={handleClientNameSave}
          isSavingClientName={isSavingClientName}
          comments={comments}
          isLoadingComments={isLoadingComments}
          isSubmittingComment={isAddingComment || isUpdatingComment}
          onAddComment={handleAddComment}
          onUpdateComment={handleUpdateComment}
          onDeleteComment={handleDeleteComment}
          activity={activity}
          isLoadingActivity={isLoadingActivity}
          onSave={handleSave}
          onComplete={() => setConfirmAction("complete")}
          onApprove={() => setConfirmAction("approve")}
          onRejectOpen={() => setRejectOpen(true)}
        />
      </div>

      <ConfirmDialog
        open={confirmAction != null}
        onOpenChange={(open) => !open && setConfirmAction(null)}
        title={
          confirmAction === "approve"
            ? "Save and approve this invoice?"
            : "Save and complete review?"
        }
        description="Your unsaved changes are saved first, then the review is finalised."
        confirmLabel={confirmAction === "approve" ? "Save and approve" : "Save and complete"}
        isSubmitting={isSaving}
        onConfirm={confirmAction === "approve" ? handleApprove : handleComplete}
      />

      <InvoiceRejectModal
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onSubmit={handleReject}
        isSubmitting={isRejecting}
      />

      {/* The same pickers the invoice list opens; saving refreshes this page's
          header through the invoice mutations' tags. */}
      <InvoiceStatusModal invoice={detail} open={statusOpen} onOpenChange={setStatusOpen} />
      <InvoiceCategoryModal invoice={detail} open={categoryOpen} onOpenChange={setCategoryOpen} />
    </div>
  );
}
